import {
  Slider,
  Box,
  Button,
  Typography,
  IconButton,
  Paper,
  Card,
  CardContent,
  useTheme,
  Collapse,
  Fade,
  Grow,
} from '@mui/material';
import { Plus, Minus, Scissors, Forward, Check } from 'lucide-mui';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { RootState } from '../../../../reducers';
import { ActionType } from '../../../../types';
import { setClipperVisibility, shareThunk } from '../../selectedVideoSlice';
import { onClickTimestamp } from '../../../../lib/commonUtils';

// Utility function to convert milliseconds to HH:MM:SS format using moment.js
const formatDuration = (ms: number): string => {
  return moment.utc(ms).format('HH:mm:ss.S');
};

const FINE_TUNE_STEP = 100; // 100ms step for fine-tuning

type ClipperActionKeys = 'share' | 'cancel';
type ClipperActionType = ActionType<ClipperActionKeys> & {
  variant: 'contained' | 'outlined';
};

const clipperActions: ClipperActionType[] = [
  {
    key: 'cancel',
    label: 'Cancel',
    variant: 'outlined',
  },
  {
    key: 'share',
    label: 'Share Clip',
    variant: 'contained',
  },
];

// Separate VideoThumbnail component
const VideoThumbnail: React.FC<{ time: number }> = ({ time }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const video = document.getElementById('video-element') as HTMLVideoElement;
    if (!video || !canvasRef.current) {
      console.log('Missing elements:', {
        video: !!video,
        canvas: !!canvasRef.current,
      });
      return;
    }

    const generateThumbnail = async () => {
      try {
        console.log('Generating thumbnail for time:', time);
        // Save current video state
        const wasPlaying = !video.paused;
        const originalTime = video.currentTime;

        // Pause video and seek to thumbnail time
        video.pause();
        video.currentTime = time / 1000;

        // Wait for seek to complete
        await new Promise<void>((resolve) => {
          const handleSeeked = () => {
            console.log('Video seeked to:', video.currentTime);
            video.removeEventListener('seeked', handleSeeked);
            resolve();
          };
          video.addEventListener('seeked', handleSeeked);
        });

        // Draw frame to canvas
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          console.error('Could not get canvas context');
          return;
        }

        // Set canvas size to match video dimensions
        const videoWidth = video.videoWidth || 160;
        const videoHeight = video.videoHeight || 90;
        const aspectRatio = videoWidth / videoHeight;

        canvas.width = 160;
        canvas.height = canvas.width / aspectRatio;

        // Clear canvas before drawing
        ctx.fillStyle = '#000000'; // Set black background
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        console.log('Drawing video frame:', {
          videoWidth,
          videoHeight,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
        });

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Restore video state
        video.currentTime = originalTime;
        if (wasPlaying) video.play();
      } catch (err) {
        console.error('Error generating thumbnail:', err);
        setError(true);
      }
    };

    generateThumbnail();
  }, [time]);

  if (error) return null;

  return (
    <Box
      sx={{
        mt: 2,
        width: '160px',
        height: '90px',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'black',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: 'black',
        }}
      />
    </Box>
  );
};

const Clipper: React.FC = () => {
  const theme = useTheme();
  const { selectedVideo } = useSelector((state: RootState) => state);
  const { duration = 0, _id: fileId } = selectedVideo.file || {};
  const durationMS = duration * 1000;
  const [values, setValues] = React.useState<number[]>([0, durationMS]);
  const dispatch = useDispatch();
  const [isClipCopied, setIsClipCopied] = useState(false);

  // Add mount animation state
  const [mounted, setMounted] = React.useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  const seekToTime = (ms: number) => {
    // Convert milliseconds to seconds for the onClickTimestamp function
    onClickTimestamp(ms / 1000);
  };

  // Reset values when modal opens
  useEffect(() => {
    setValues([0, durationMS]);
  }, [durationMS]);

  // Add state to track recent changes
  const [recentChange, setRecentChange] = React.useState<0 | 1 | null>(null);

  // Update handleChange to include visual feedback
  const handleChange = (event: Event, newValue: number | number[]) => {
    const newValues = newValue as number[];
    setValues(newValues);
    seekToTime(newValues[0]);
    // Show feedback for the thumb that changed
    setRecentChange(newValues[0] !== values[0] ? 0 : 1);
    // Clear feedback after a short delay
    setTimeout(() => setRecentChange(null), 1000);
  };

  // Update handleFineTune to include visual feedback
  const handleFineTune = useCallback(
    (index: 0 | 1, direction: 1 | -1) => {
      setValues((prev) => {
        const newValues = [...prev];
        newValues[index] += FINE_TUNE_STEP * direction;

        if (index === 0) {
          newValues[0] = Math.max(
            0,
            Math.min(newValues[0], newValues[1] - FINE_TUNE_STEP)
          );
          seekToTime(newValues[0]);
        } else {
          newValues[1] = Math.min(
            durationMS,
            Math.max(newValues[1], newValues[0] + FINE_TUNE_STEP)
          );
        }

        return newValues;
      });
      setRecentChange(index);
      setTimeout(() => setRecentChange(null), 1000);
    },
    [durationMS]
  );

  // Add click handlers for the time displays
  const handleTimeClick = (index: 0 | 1) => {
    seekToTime(values[index]);
  };

  const handleKeyDown = useCallback(
    ({ key }: KeyboardEvent) => {
      const { activeElement } = document;
      if (!activeElement?.id.includes('clip-slider')) return;

      switch (key) {
        case 'ArrowLeft':
          handleFineTune(0, -1);
          break;
        case 'ArrowRight':
          handleFineTune(0, 1);
          break;
        case 'ArrowDown':
          handleFineTune(1, -1);
          break;
        case 'ArrowUp':
          handleFineTune(1, 1);
          break;
        default:
          break;
      }
    },
    [handleFineTune]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleAction = async (key: ClipperActionKeys) => {
    switch (key) {
      case 'share':
        try {
          const startTime = values[0] / 1000;
          const endTime = values[1] / 1000;

          // Get existing shareId or create new one
          const shareId = selectedVideo.file.share?.shareId;
          if (shareId) {
            const basePath = window.location.pathname.replace(/\/$/, ''); // Remove trailing slash if present
            const shareUrl = `${window.location.origin}${basePath}/#/shared/${shareId}?start=${startTime}&end=${endTime}`;
            await navigator.clipboard.writeText(shareUrl);
            setIsClipCopied(true);
            setTimeout(() => setIsClipCopied(false), 2000);
            // Open in new window after a short delay
            setTimeout(() => {
              window.open(shareUrl, '_blank', 'noopener');
            }, 500);
          } else {
            // Only dispatch if we don't have a shareId
            await dispatch(shareThunk({ fileId, copyUrlToClipboard: false }));
            const newShareId = selectedVideo.file.share?.shareId;
            if (newShareId) {
              const basePath = window.location.pathname.replace(/\/$/, ''); // Remove trailing slash if present
              const shareUrl = `${window.location.origin}${basePath}/#/shared/${newShareId}?start=${startTime}&end=${endTime}`;
              await navigator.clipboard.writeText(shareUrl);
              setIsClipCopied(true);
              setTimeout(() => setIsClipCopied(false), 2000);
              // Open in new window after a short delay
              setTimeout(() => {
                window.open(shareUrl, '_blank', 'noopener');
              }, 500);
            }
          }
        } catch (error) {
          console.error('Failed to share clip:', error);
        }
        break;
      case 'cancel':
        setMounted(false); // Trigger collapse animation
        // Wait for animation to complete before resetting values and closing
        setTimeout(() => {
          setValues([0, durationMS]);
          dispatch(setClipperVisibility(false));
        }, 300);
        break;
      default:
        break;
    }
  };

  const clipDuration = values[1] - values[0];

  const getButtonIcon = (key: ClipperActionKeys, copied: boolean) => {
    if (key !== 'share') return undefined;
    return copied ? <Check /> : <Forward />;
  };

  return (
    <Collapse in={mounted} timeout={300}>
      <Card
        sx={{
          width: '100%',
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          transform: 'translateZ(0)', // Enables hardware acceleration
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Fade in={mounted} timeout={400}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1.5 }}
            >
              <Scissors
                sx={{
                  color: theme.palette.primary.main,
                  opacity: 0.9,
                  fontSize: 20,
                }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  letterSpacing: '0.0075em',
                }}
              >
                Share Clip
              </Typography>
            </Box>
          </Fade>

          <Grow in={mounted} timeout={500}>
            <Box sx={{ width: '100%', px: 4 }}>
              <Slider
                size="small"
                min={0}
                id="clip-slider"
                max={durationMS}
                value={values}
                step={FINE_TUNE_STEP}
                getAriaLabel={() => 'Clip duration'}
                onChange={handleChange}
                valueLabelDisplay="auto"
                valueLabelFormat={formatDuration}
                marks={[
                  { value: values[0], label: formatDuration(values[0]) },
                  { value: values[1], label: formatDuration(values[1]) },
                ]}
                sx={{
                  '& .MuiSlider-thumb': {
                    width: 8,
                    height: 24,
                    borderRadius: 0.5,
                    cursor: 'grab',
                    '&:active': {
                      cursor: 'grabbing',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'currentColor',
                    },
                  },
                  '& .MuiSlider-track': {
                    height: 16,
                    border: 'none',
                    borderRadius: 1,
                  },
                  '& .MuiSlider-rail': {
                    height: 16,
                    opacity: 0.8,
                    backgroundColor: theme.palette.secondary.main,
                    borderRadius: 1,
                  },
                  '& .MuiSlider-mark': {
                    display: 'none',
                  },
                  '& .MuiSlider-markLabel': {
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    marginTop: 2,
                  },
                  py: 3,
                }}
              />
            </Box>
          </Grow>

          <Fade in={mounted} timeout={600}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                px: 3,
                mt: 3,
                gap: 2,
              }}
            >
              {[
                { label: 'Start point', index: 0 as 0, value: values[0] },
                { label: 'End point', index: 1 as 1, value: values[1] },
              ].map(({ label, index, value }) => (
                <Paper
                  key={label}
                  elevation={0}
                  sx={{
                    flex: 1,
                    p: 2,
                    backgroundColor: theme.palette.background.default,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{
                      display: 'block',
                      mb: 1,
                      textAlign: 'center',
                    }}
                  >
                    {label}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        data-testid="RemoveIcon"
                        onClick={() => handleFineTune(index, -1)}
                        sx={{
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Minus fontSize="small" />
                      </IconButton>
                      <Typography
                        variant="body2"
                        onClick={() => handleTimeClick(index)}
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 500,
                          minWidth: '85px',
                          textAlign: 'center',
                          py: 0.5,
                          px: 1,
                          borderRadius: '4px',
                          transition: theme.transitions.create('color', {
                            duration: 200,
                          }),
                          color:
                            recentChange === index
                              ? 'primary.main'
                              : 'text.primary',
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        {formatDuration(value)}
                      </Typography>
                      <IconButton
                        size="small"
                        data-testid="AddIcon"
                        onClick={() => handleFineTune(index, 1)}
                        sx={{
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Plus fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Only show thumbnail for videos */}
                    {selectedVideo.file?.streams?.includes('video') && (
                      <VideoThumbnail time={value} />
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          </Fade>

          <Fade in={mounted} timeout={700}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                mt: 2,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  backgroundColor: theme.palette.background.default,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  textAlign: 'center',
                  width: {
                    xs: '100%',
                    sm: 'calc(50% - 16px)',
                  },
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Final duration: {formatDuration(clipDuration)}
                </Typography>
              </Paper>
            </Box>
          </Fade>

          <Fade in={mounted} timeout={800}>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mt: 3,
                justifyContent: 'flex-end',
              }}
            >
              {clipperActions.map(({ key, label, variant }) => (
                <Button
                  key={key}
                  variant={variant}
                  onClick={() => handleAction(key)}
                  color={key === 'cancel' ? 'secondary' : 'primary'}
                  size="medium"
                  startIcon={getButtonIcon(key, isClipCopied)}
                  sx={{
                    minWidth: '88px',
                    fontWeight: 500,
                    transition: theme.transitions.create(
                      ['transform', 'box-shadow'],
                      { duration: 100 }
                    ),
                    '&:hover': {
                      transform: 'translateY(-1px)',
                    },
                    '&:active': {
                      transform: 'translateY(1px)',
                    },
                  }}
                >
                  {isClipCopied && key === 'share' ? 'Copied!' : label}
                </Button>
              ))}
            </Box>
          </Fade>
        </CardContent>
      </Card>
    </Collapse>
  );
};

export default Clipper;
